import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { CalcBreakdown } from "@/components/mobilfunk/CalcBreakdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Plus, FileSignature, RefreshCw } from "lucide-react";

function CalcBadge({ status }) {
  if (status === "pruefung") return <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20">Zur Prüfung</Badge>;
  return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20">Berechnet</Badge>;
}

export default function MfContracts() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "mobilfunk:contracts");
  const [q, setQ] = useState("");
  const [create, setCreate] = useState(false);
  const [detail, setDetail] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["mf-contracts", q], queryFn: async () => (await api.get(`/mobilfunk/contracts?q=${encodeURIComponent(q)}`)).data });

  const columns = [
    { key: "customer", header: "Kunde", render: (c) => (
      <div className="min-w-0"><div className="truncate font-medium">{c.customer_name}</div>
        <div className="text-xs text-muted-foreground">{c.customer_type} · {c.tariff_name} · {fmtDate(c.closing_date)}</div></div>
    ) },
    { key: "commission", header: "Provision", render: (c) => fmtEUR(c.commission_eur) },
    { key: "calc", header: "Berechnung", render: (c) => <CalcBadge status={c.calc_status} /> },
  ];

  return (
    <div data-testid="mf-contracts">
      <PageHeader title="Vertragsabschlüsse" description={`${data?.total ?? 0} Abschlüsse`}
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setCreate(true)} data-testid="new-contract-button"><Plus className="h-4 w-4" />Neuer Abschluss</Button>} />
      <div className="mb-4"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kunde, Auftragsnummer…" className="sm:max-w-sm" data-testid="contract-search" /></div>
      <DataTable columns={columns} rows={data?.items} loading={isLoading} testIdPrefix="contract-row" onRowClick={(c) => setDetail(c.id)} />
      <CreateContractDialog open={create} onClose={() => setCreate(false)} onDone={(id) => { setCreate(false); qc.invalidateQueries({ queryKey: ["mf-contracts"] }); setDetail(id); }} />
      <ContractDrawer cid={detail} onClose={() => setDetail(null)} canRecalc={write} onChanged={() => qc.invalidateQueries({ queryKey: ["mf-contracts"] })} />
    </div>
  );
}

function CreateContractDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ customer_id: "", tariff_id: "", closing_date: "", activation_date: "", channel: "", promotion_id: "", device_id: "", external_order_no: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const { data: customers } = useQuery({ queryKey: ["mf-cust"], queryFn: async () => (await api.get("/customers?limit=200")).data, enabled: open });
  const { data: tariffs } = useQuery({ queryKey: ["mf-tariffs-min"], queryFn: async () => (await api.get("/mobilfunk/tariffs")).data, enabled: open });
  const { data: promos } = useQuery({ queryKey: ["mf-promos-min"], queryFn: async () => (await api.get("/mobilfunk/promotions?status=freigegeben")).data, enabled: open });
  const { data: devices } = useQuery({ queryKey: ["mf-devices-min"], queryFn: async () => (await api.get("/wawi/devices?limit=100")).data, enabled: open });

  const custList = customers?.items || [];
  const tariffList = (tariffs?.items || []).filter((t) => t.current_status === "freigegeben");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_id || !f.tariff_id || !f.closing_date) return toast.error("Kunde, Tarif und Abschlussdatum sind erforderlich");
    try {
      const { data } = await api.post("/mobilfunk/contracts", f);
      toast.success(`Abschluss erfasst – Provision ${data.calculation.status === "pruefung" ? "zur Prüfung" : fmtEUR(data.calculation.total_amount_eur)}`);
      setF({ customer_id: "", tariff_id: "", closing_date: "", activation_date: "", channel: "", promotion_id: "", device_id: "", external_order_no: "" });
      onDone(data.id);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="create-contract-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSignature className="h-4 w-4" />Neuer Vertragsabschluss</DialogTitle>
          <DialogDescription>Der Konditionssnapshot wird beim Abschluss eingefroren; spätere Importe ändern diesen Abschluss nicht.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Kunde</Label>
            <Select value={f.customer_id} onValueChange={(v) => set("customer_id", v)}>
              <SelectTrigger data-testid="contract-customer-select"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>{custList.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim()}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label>Tarif (freigegeben)</Label>
            <Select value={f.tariff_id} onValueChange={(v) => set("tariff_id", v)}>
              <SelectTrigger data-testid="contract-tariff-select"><SelectValue placeholder="Tarif wählen" /></SelectTrigger>
              <SelectContent>{tariffList.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({fmtEUR(t.monthly_fee)})</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Abschlussdatum</Label><Input type="date" value={f.closing_date} onChange={(e) => set("closing_date", e.target.value)} data-testid="contract-closing-date" /></div>
            <div className="space-y-1.5"><Label>Aktivierungsdatum</Label><Input type="date" value={f.activation_date} onChange={(e) => set("activation_date", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Vertriebskanal</Label><Input value={f.channel} onChange={(e) => set("channel", e.target.value)} placeholder="z. B. shop" data-testid="contract-channel" /></div>
            <div className="space-y-1.5"><Label>Auftragsnummer</Label><Input value={f.external_order_no} onChange={(e) => set("external_order_no", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Aktion</Label>
              <Select value={f.promotion_id || "none"} onValueChange={(v) => set("promotion_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Keine</SelectItem>{(promos?.items || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Gerät (optional)</Label>
              <Select value={f.device_id || "none"} onValueChange={(v) => set("device_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(devices?.items || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.article_name} {d.imei1 ? `· ${d.imei1}` : ""}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="contract-save">Abschluss erfassen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContractDrawer({ cid, onClose, canRecalc, onChanged }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mf-contract", cid], queryFn: async () => (await api.get(`/mobilfunk/contracts/${cid}`)).data, enabled: !!cid });
  const c = data?.contract;
  const recalc = async () => {
    try { await api.post(`/mobilfunk/contracts/${cid}/recalculate`); toast.success("Neu berechnet"); qc.invalidateQueries({ queryKey: ["mf-contract", cid] }); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Sheet open={!!cid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="contract-drawer">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><FileSignature className="h-4 w-4" />{c?.customer_name}</SheetTitle></SheetHeader>
        {c && (
          <div className="mt-4 space-y-4">
            <div className="rounded-lg border border-border p-3 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Konditionssnapshot (eingefroren)</div>
              <div className="mt-1.5 text-muted-foreground">
                Tarifversion v{c.snapshot?.tariff_version?.version_no} · ab {fmtDate(c.snapshot?.tariff_version?.valid_from)} · {fmtEUR((c.snapshot?.tariff_version?.monthly_fee_cents || 0) / 100)}/Mon.
                {c.snapshot?.promotion && <> · Aktion: {c.snapshot.promotion.name}</>}
              </div>
              <div className="mt-1 text-muted-foreground">Abschluss {fmtDate(c.closing_date)}{c.activation_date ? ` · Aktivierung ${fmtDate(c.activation_date)}` : ""} · Kanal {c.channel || "—"}</div>
            </div>
            <CalcBreakdown calc={c.calculation} />
            {canRecalc && <Button variant="outline" className="gap-1.5" onClick={recalc} data-testid="contract-recalc"><RefreshCw className="h-4 w-4" />Neu berechnen (reproduzierbar)</Button>}
            {data?.calculation_history?.length > 1 && <div className="text-xs text-muted-foreground">{data.calculation_history.length} Berechnungen protokolliert.</div>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
